import { useAuth } from "@clerk/clerk-react";
import {
  AccountBalanceOutlined,
  AdminPanelSettingsOutlined,
  AssessmentOutlined,
  AssignmentTurnedInOutlined,
  CampaignOutlined,
  Close,
  ContactsOutlined,
  GroupsOutlined,
  HelpOutline,
  HistoryOutlined,
  InsightsOutlined,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  LoyaltyOutlined,
  MarkAsUnreadOutlined,
  Menu,
  PaymentsOutlined,
  ReceiptLongOutlined,
  VolunteerActivismOutlined,
} from "@mui/icons-material";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import { Button, Divider } from "@mui/material";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { useCallback, useState } from "react";
import { Outlet, useLocation, useMatch, useNavigate, useSearchParams } from "react-router-dom";
import AccountTopbarIdentity from "@/components/AccountTopbarIdentity";
import { HelpCenter } from "@/components/help/HelpCenter";
import { FeaturedSeasonalGiftsIcon } from "@/components/icons";
import { ImpactControlBar } from "@/components/impactReport/ImpactControlBar";
import ImpersonationBanner, { IMP_BAR_HEIGHT } from "@/components/impersonation/Banner";
import { PermissionGate } from "@/components/PermissionGate";
import PlanBadge from "@/components/plan/PlanBadge";
import FundingBanner from "@/components/topups/FundingBanner";
import SpotlightTour from "@/components/tour/SpotlightTour";
import GfgButton from "@/components/ui/GfgButton";
import { isProduction } from "@/config/publicEnv";
import { GFG_LOGO, releaseCampaignBuilderDraft } from "@/constants/campaignBuilder";
import {
  IMPACT_REPORT_JOB_APP_BAR_TITLE,
  IMPACT_REPORT_JOB_ROUTE,
  IMPACT_REPORT_ROUTE_PATH,
} from "@/constants/impactReport";
import { REPORTS_ROUTE_PATH } from "@/constants/reportsRedemptions";
import { TOUR_TARGET_IDS } from "@/constants/tour";
import { useCollectionsContext } from "@/context/CollectionsContext";
import { useHelpPanel } from "@/context/HelpPanelContext";
import { TourProvider } from "@/context/TourContext";
import { useBaseTierCollections } from "@/hooks/useBaseTierCollections";
import { useCollections } from "@/hooks/useCollections";
import { usePermissions } from "@/hooks/usePermissions";
import type { AccessScope } from "@/interfaces/permissions";
import { useIsMobile } from "../hooks/useMediaQuery";
import { DrawerUserProfile } from "./DrawerUserProfile";

const DRAWER_WIDTH_FULL = 240;
const DRAWER_WIDTH_MINI = 50;

// Single source of truth for the sessionStorage key — read and write helpers below share it.
const SIDEBAR_COLLAPSED_KEY = "sidebarCollapsed";

// No stored preference (or an unrecognized value) means open.
export function readSidebarOpenState(saved: string | null): boolean {
  return saved !== "true";
}

// Stores the "collapsed" flag, i.e. the inverse of the open state being persisted.
export function writeSidebarCollapsedValue(isOpen: boolean): string {
  return (!isOpen).toString();
}

// Navigation items
// Items with a `permission` field are wrapped in PermissionGate and hidden
// from users who lack that permission. All other items are always visible.
const mainNavItems: {
  text: string;
  icon: React.ComponentType;
  path: string;
  permission?: { action: string; subject: string; scope?: AccessScope };
}[] = [
  { text: "Dashboard", icon: DashboardOutlinedIcon, path: "/dashboard" },
  {
    text: "Users & Orgs",
    icon: AdminPanelSettingsOutlined,
    path: "/users-orgs",
    permission: { action: "view", subject: "USER", scope: "all" },
  },
  { text: "My Campaigns", icon: LoyaltyOutlined, path: "/campaigns" },
  { text: "Thank You Notes", icon: MarkAsUnreadOutlined, path: "/thanks-report" },
  {
    text: "Impact",
    icon: VolunteerActivismOutlined,
    path: IMPACT_REPORT_ROUTE_PATH,
    // Same gate as the /impact route's own PermissionGuard — without it, users who'd be
    // redirected away from the route would still see the link.
    permission: { action: "view", subject: "REPORT" },
  },
  {
    text: "Reports",
    icon: AssessmentOutlined,
    path: REPORTS_ROUTE_PATH,
    // Same gate as the /reports route's PermissionGuard.
    permission: { action: "view", subject: "REPORT" },
  },
  { text: "Collections", icon: CardGiftcardIcon, path: "/collections" },
  {
    text: "Finance",
    icon: AccountBalanceOutlined,
    path: "/finance",
    // FINANCE/view at scope "all" — GFG only.
    //
    // It was CREDIT/view, which Client Admin and Manager legitimately hold: they need CREDIT for
    // wallets and for buying credits, so the fix is not to take that away. The gate was simply on
    // the wrong subject. This page is the CROSS-ORG invoice blotter — GFG's view of every client's
    // billing — and a client reaches their own invoices from Account, on the Invoices tab.
    permission: { action: "view", subject: "FINANCE", scope: "all" },
  },
  {
    text: "Accounting",
    icon: ReceiptLongOutlined,
    path: "/accounting",
    // FINANCE/view pinned to scope "all" — the subledger holds every client's entries side by side,
    // so an org-level grant must not open it. Same pin on the API route.
    permission: { action: "view", subject: "FINANCE", scope: "all" },
  },
  {
    text: "Campaign Tester",
    icon: ScienceOutlinedIcon,
    path: "/campaign-tester",
    // Same pin as Accounting: this surface can move real credits and post real entries, so an
    // org-level grant must never reach it. The API is additionally off unless
    // ENABLE_CAMPAIGN_TESTER=true. Scaffolding — removed with the tester.
    permission: { action: "view", subject: "FINANCE", scope: "all" },
  },
];

const footerNavItems: { text: string; icon: React.ComponentType<any>; path: string }[] = [];

// Proper topbar titles for routes whose path doesn't title-case cleanly.
const PAGE_TITLES: Record<string, string> = {
  "/users-orgs": "Users & Orgs",
  "/thanks-report": "Thank You Notes",
  "/admin/collections": "Collections",
};

function DrawerLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Collections tier "send" gate — the shared provider owns the international upsell modal.
  const { sendCollection } = useCollectionsContext();
  const isMobile = useIsMobile();
  const { actor } = useAuth();
  const { isGfg } = usePermissions();
  const isImpersonating = !!actor;
  const { isOpen: isHelpPanelOpen, toggle: toggleHelpPanel } = useHelpPanel();

  // Admin console is now explicitly in main navigation for GFG-only.
  const footerItems = footerNavItems;

  // Desktop: persist open/closed state. Mobile: always open when visible
  const [desktopOpen, setDesktopOpen] = useState(() =>
    readSidebarOpenState(sessionStorage.getItem(SIDEBAR_COLLAPSED_KEY)),
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [footerExpanded, setFooterExpanded] = useState(false);

  // Active nav item background varies by context: impersonation, GFG admin, or default client.
  let activeBgClass = "bg-primary-active";
  if (isImpersonating) {
    activeBgClass = "bg-dark-dust";
  } else if (isGfg) {
    activeBgClass = "bg-rodeo-black";
  }

  const activeNavItems: typeof mainNavItems =
    isGfg && !isImpersonating
      ? [
          { text: "GfG Dashboard", icon: InsightsOutlined, path: "/gfg-dashboard" },
          { text: "Users & Orgs", icon: GroupsOutlined, path: "/users-orgs" },
          { text: "Campaign Manager", icon: CampaignOutlined, path: "/campaigns" },
          { text: "Collections", icon: CardGiftcardIcon, path: "/admin/collections" },
          {
            text: "Contacts",
            icon: ContactsOutlined,
            path: "/contacts",
            // Same gate the route enforces (App.tsx) and the backend requires on
            // /admin/contacts — the cross-org recipient list is GFG-only.
            permission: { action: "view", subject: "USER", scope: "all" },
          },
          { text: "Financials", icon: PaymentsOutlined, path: "/finance" },
          {
            text: "Actions",
            icon: AssignmentTurnedInOutlined,
            path: "/actions",
            // approve/CREDIT at scope "all" — the same gate as the route and the API. Being IN the
            // approvers list is what lets someone decide; this only shows the surface.
            permission: { action: "approve", subject: "CREDIT", scope: "all" },
          },
          { text: "Audit Log", icon: HistoryOutlined, path: "/audit-log" },
          {
            text: "Accounting",
            icon: ReceiptLongOutlined,
            path: "/accounting",
            permission: { action: "view", subject: "FINANCE", scope: "all" },
          },
          {
            text: "Campaign Tester",
            icon: ScienceOutlinedIcon,
            path: "/campaign-tester",
            permission: { action: "view", subject: "FINANCE", scope: "all" },
          },
          {
            text: "System",
            icon: SettingsOutlined,
            path: "/system",
            // Super-admin only, same gate as the route and the API. Rotating the default cause list
            // redirects every client's unclaimed credits, so a GFG Admin running the accounts does
            // not see this at all.
            permission: { action: "modify", subject: "CHARITY_DESTINATION", scope: "all" },
          },
        ]
      : mainNavItems;

  // Determine drawer state based on device
  const isDrawerOpen = isMobile ? true : desktopOpen;
  const drawerWidth = isDrawerOpen ? DRAWER_WIDTH_FULL : DRAWER_WIDTH_MINI;

  const handleDesktopDrawerToggle = () => {
    const nextOpen = !desktopOpen;
    setDesktopOpen(nextOpen);
    sessionStorage.setItem(SIDEBAR_COLLAPSED_KEY, writeSidebarCollapsedValue(nextOpen));
  };

  const handleMobileDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const toggleFooterMenu = () => {
    setFooterExpanded(!footerExpanded);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    // Close mobile drawer after navigation
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleSupportClick = () => {
    // Close the temporary mobile drawer BEFORE opening the panel — its own focus
    // enforcement would otherwise fight the panel's focus trap (same ordering as
    // handleNavigation above).
    if (isMobile) {
      setMobileOpen(false);
    }
    toggleHelpPanel();
  };

  const { data: collections = [] } = useCollections();

  const collectionMatch = useMatch("/collections/:id");
  const collectionId = collectionMatch?.params?.id;
  const activeCollectionName = collectionId
    ? (collections.find((c) => c.id === collectionId)?.title ?? "Collection")
    : null;
  const isCollectionDetailView = !!collectionMatch;
  // GFG Collections Settings (/admin/collections and /admin/collections/:id) — plain "Collections" title.
  const isAdminCollectionsPath =
    location.pathname === "/admin/collections" ||
    location.pathname.startsWith("/admin/collections/");

  // Tier detail (/collections/tier/:budget) gets the SAME app-bar treatment as a single
  // collection — the 80px bar, back link, title + action button — but its own copy: the back
  // link reads "Back to Tier Gallery" and the title is the tier's display name (from the same
  // base-tier cards the gallery renders), falling back to a "$<budget> Collection" label.
  const { data: tierCards = [] } = useBaseTierCollections();
  const tierMatch = useMatch("/collections/tier/:budget");
  const tierBudget = tierMatch?.params?.budget;
  const activeTierName = tierBudget
    ? (tierCards.find((t) => String(t.budget) === tierBudget)?.title ?? `$${tierBudget} Collection`)
    : null;
  const isTierDetailView = !!tierMatch;

  // Layout is shared by both detail views; the copy differs per view.
  const isDetailView = isCollectionDetailView || isTierDetailView;
  const detailBackLabel = isTierDetailView ? "Back to Tier Gallery" : "Back to Collection Gallery";
  const detailName = isTierDetailView ? activeTierName : activeCollectionName;

  /**
   * The Impact route swaps the app bar's CONTENTS for the report's own control bar — same
   * `Toolbar`, same standard height, same fixed geometry. A content swap, never a second bar and
   * never a layout change, so no other route's app bar moves by a pixel.
   */
  const isImpactReportView = !!useMatch(IMPACT_REPORT_ROUTE_PATH);
  // The emailed report link keeps the ordinary app bar (no controls), but its UUID path would
  // otherwise slugify into the title.
  const isImpactReportJobView = !!useMatch(IMPACT_REPORT_JOB_ROUTE);

  // App Bar height grows by one row on the collection detail view. The Impact route keeps the
  // STANDARD height: its controls are 44px inside a 64px bar, which is exactly why the swap needs
  // no extra room.
  const appBarHeight = isDetailView ? 80 : 64;

  const isActivePath = (path: string) => {
    return location.pathname === path || (path !== "/" && location.pathname.startsWith(`${path}/`));
  };

  const handleCreateCampaign = useCallback(() => {
    // "+" means NEW: drop any stashed campaign so the builder does not reopen the last one.
    releaseCampaignBuilderDraft();
    navigate("/campaign/create");
  }, [navigate]);

  const drawerContent = (
    <div className="flex flex-col h-full">
      {/* Drawer Header */}
      <div className={`flex items-center py-3 min-h-16 shrink-0 ${isDrawerOpen ? "px-5" : "px-2"}`}>
        {isDrawerOpen && (
          <>
            {/* Spacer mirrors the collapse-button width so the logo stays centered */}
            <span className="w-10 shrink-0" aria-hidden="true" />
            <span className="flex flex-1 justify-center min-w-0 overflow-hidden">
              <img
                src={GFG_LOGO}
                alt="Gifts for Good Forward"
                className="h-10 max-w-full object-contain"
              />
            </span>
          </>
        )}
        <IconButton
          onClick={isMobile ? handleMobileDrawerToggle : handleDesktopDrawerToggle}
          className={`shrink-0 ${isDrawerOpen ? "" : "mx-auto"}`}
          aria-label={
            isMobile ? "Close menu" : isDrawerOpen ? "Collapse sidebar" : "Expand sidebar"
          }
          sx={
            !isMobile
              ? {
                  color: "var(--sidebar-foreground)",
                  "&:hover": { backgroundColor: "grey.100" },
                }
              : undefined
          }
        >
          {isMobile ? (
            <Close />
          ) : isDrawerOpen ? (
            <KeyboardDoubleArrowLeft className="text-primary-active" />
          ) : (
            <KeyboardDoubleArrowRight className="text-primary-active" />
          )}
        </IconButton>
      </div>
      {/* Main Navigation */}
      <Divider
        sx={{ borderBottomWidth: 3, marginBottom: isMobile ? 0 : 3 }}
        className="text-primary-active"
      />
      <List
        id={TOUR_TARGET_IDS.sidebarNav}
        className="flex-1 min-h-0 overflow-y-auto"
        sx={{ paddingY: 0 }}
      >
        {activeNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = isActivePath(item.path);

          const listItem = (
            <ListItem
              key={item.text}
              disablePadding
              className={`${isActive ? activeBgClass : ""} ${isMobile ? "my-0" : "my-2"}`}
            >
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                className={`min-h-12 ${isDrawerOpen ? "px-6" : "justify-center px-2"}`}
              >
                <ListItemIcon
                  className={`min-w-0 ${
                    isActive ? "text-white" : "text-gray-700"
                  } ${isDrawerOpen ? "mr-6" : "mr-0"}`}
                >
                  <Icon />
                </ListItemIcon>
                {isDrawerOpen && (
                  <ListItemText
                    primary={item.text}
                    className={isActive ? "text-white" : "text-gray-900"}
                    slotProps={{
                      primary: {
                        sx: { fontWeight: 400, fontSize: "16px", lineHeight: "20px" },
                      },
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );

          if (item.permission) {
            return (
              <PermissionGate
                key={item.text}
                action={item.permission.action}
                subject={item.permission.subject}
                scope={item.permission.scope}
              >
                {listItem}
              </PermissionGate>
            );
          }

          return listItem;
        })}
      </List>

      {/* Sidebar Footer */}
      <div className="mt-auto shrink-0">
        {footerItems.length > 0 && (
          <div className="border-t border-gray-200">
            {/* Main Footer Links */}
            <List className="flex-1">
              {footerItems.map((item) => {
                const Icon = item.icon;
                const isActive = isActivePath(item.path);

                return (
                  <ListItem
                    key={item.text}
                    disablePadding
                    className={`${isActive ? activeBgClass : ""} ${isMobile ? "my-0" : "my-2"}`}
                  >
                    <ListItemButton
                      onClick={() => handleNavigation(item.path)}
                      className={`min-h-12 ${isDrawerOpen ? "px-6" : "justify-center px-2"}`}
                    >
                      <ListItemIcon
                        className={`min-w-0 ${
                          isActive ? "text-white" : "text-gray-700"
                        } ${isDrawerOpen ? "mr-6" : "mr-0"}`}
                      >
                        <Icon />
                      </ListItemIcon>
                      {isDrawerOpen && (
                        <ListItemText
                          primary={item.text}
                          slotProps={{
                            primary: {
                              sx: { fontWeight: 400, fontSize: "16px", lineHeight: "20px" },
                            },
                          }}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </div>
        )}
        <Divider className="border-gray-200" />
        {/* User Profile */}
        <DrawerUserProfile
          isDrawerOpen={isDrawerOpen}
          isMobile={isMobile}
          footerExpanded={footerExpanded}
          toggleFooterMenu={toggleFooterMenu}
          handleNavigation={handleNavigation}
          isActivePath={isActivePath}
        />

        {/* Support — opens the Help Center. Internal-only for now: hidden in production (env
            reported by the API via /api/config), shown on staging/dev. Mirrors the HelpBubble gate
            in HelpCenter so both Help Center entry points appear/disappear together. */}
        {!isProduction() && (
          <List component="div" disablePadding>
            <ListItem
              disablePadding
              className={`${isHelpPanelOpen ? "bg-dark-dust" : ""} ${isMobile ? "my-0" : ""}`}
            >
              <ListItemButton
                onClick={handleSupportClick}
                aria-haspopup="dialog"
                aria-expanded={isHelpPanelOpen}
                className={`min-h-[44px] ${isDrawerOpen ? "px-6" : "justify-center px-2"}`}
              >
                <ListItemIcon
                  className={`min-w-0 ${
                    isHelpPanelOpen ? "text-white" : "text-gray-600"
                  } ${isDrawerOpen ? "mr-4" : "mr-0"}`}
                >
                  <HelpOutline fontSize="small" />
                </ListItemIcon>
                {isDrawerOpen && (
                  <ListItemText
                    primary="Support"
                    className={isHelpPanelOpen ? "text-white" : "text-gray-700"}
                    slotProps={{
                      primary: { sx: { fontSize: "14.5px", fontWeight: 400 } },
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          </List>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen">
      {/* Impersonation banner — fixed at top, above everything */}
      <ImpersonationBanner />

      {/* App Bar */}
      <div
        className="fixed right-0 z-50 bg-white border-b border-gray-200 transition-all duration-200"
        style={{
          left: isMobile ? 0 : drawerWidth,
          top: isImpersonating ? IMP_BAR_HEIGHT : 0,
        }}
      >
        <Toolbar
          sx={{
            minHeight: `${appBarHeight}px !important`,
            px: "16px",
            py: isDetailView ? "12px" : 0,
            display: "flex",
            alignItems: isDetailView ? "flex-start" : "center",
            gap: 1,
          }}
        >
          {/* Mobile hamburger */}
          <IconButton
            onClick={isMobile ? handleMobileDrawerToggle : handleDesktopDrawerToggle}
            edge="start"
            className={isMobile ? "" : "hidden"}
            aria-label="Open menu"
            sx={{ mt: isDetailView ? "4px" : 0, flexShrink: 0 }}
          >
            <Menu />
          </IconButton>

          {/* On the Impact route the title + "Send Gifts" content is REPLACED by the report's
              own control bar. Every other route keeps the block below verbatim. */}
          {isImpactReportView ? (
            <ImpactControlBar />
          ) : (
            <>
              {/* ── Title area ── (holds the flex-1 the title used to own, so the
                  plan pill can sit beside the title instead of at the far edge) */}
              <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 1.5 }}>
                {isDetailView ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      minWidth: 0,
                    }}
                  >
                    {/* Back to gallery — copy differs for the tier vs single-collection view */}
                    <Box
                      component="button"
                      onClick={() => navigate("/collections")}
                      aria-label={detailBackLabel}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        p: 0,
                        width: "fit-content",
                        "&:focus-visible": {
                          outline: "2px solid var(--color-primary-active)",
                          outlineOffset: "2px",
                          borderRadius: "2px",
                        },
                      }}
                    >
                      <KeyboardBackspaceIcon
                        sx={{ fontSize: 18, color: "var(--color-primary-active)" }}
                      />
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color: "var(--color-primary-active)",
                          lineHeight: "20px",
                        }}
                      >
                        {detailBackLabel}
                      </Typography>
                    </Box>
                    {/* Collection / tier name */}
                    <Typography
                      sx={{
                        fontFamily: "var(--font-crimson)",
                        fontSize: isMobile ? "18px" : "20px",
                        fontWeight: 400,
                        color: "var(--color-text-primary)",
                        lineHeight: 1,
                        letterSpacing: "-0.4px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {detailName}
                    </Typography>
                  </Box>
                ) : location.pathname === "/account" ? (
                  // Account owns its own icon + name + pill (GFG-237) — replaces both the
                  // generic h1 below and the sibling <PlanBadge /> for this route only.
                  <AccountTopbarIdentity />
                ) : (
                  // min-w-0 + truncate so a long title yields space to the pill at 375px
                  // instead of wrapping and pushing the toolbar out of its fixed height.
                  <h1 className="text-3xl font-medium ml-3 min-w-0 truncate">
                    {isImpactReportJobView
                      ? IMPACT_REPORT_JOB_APP_BAR_TITLE
                      : isAdminCollectionsPath
                        ? "Collections"
                        : (PAGE_TITLES[location.pathname] ??
                          location.pathname.charAt(1).toUpperCase() + location.pathname.slice(2))}
                  </h1>
                )}

                {/* DIV-7761 — plan pill (FR-2). Renders null for GFG staff and until
                    the ["tier"] query has data, so nothing here is conditional. Account
                    renders its own pill via AccountTopbarIdentity above, so this is skipped
                    there to avoid a duplicate. */}
                {location.pathname !== "/account" && <PlanBadge />}
              </Box>

              {/* ── Action button ── Gated on create CAMPAIGN: both entry points open the campaign
                  builder (the create route is already gated server-side), so a read-only role that
                  cannot create campaigns (VIEW_ONLY / ORG_VIEW_ONLY) sees no launch button at all.

                  `clientOnly` covers BOTH buttons on purpose. GFG staff hold create CAMPAIGN at
                  scope "all" and may genuinely launch one — but by impersonating the client, never
                  from their own console, so neither entry point belongs in a staff session. Gating
                  only "Send Gifts" would leave "Send This Collection" as the same door under
                  another label. Impersonating restores both. */}
              <PermissionGate action="create" subject="CAMPAIGN" clientOnly>
                {isDetailView ? (
                  // Opens the builder at Step 1 with this tier pre-selected. `tierBudget` is only set
                  // on the tier route (/collections/tier/:budget) — the single-collection detail view
                  // is orphaned, so no budget source is wired for it.
                  <GfgButton
                    variant="primary"
                    data-testid="send-collection-btn"
                    aria-label={`Send ${detailName}`}
                    className="mt-1 shrink-0 whitespace-nowrap"
                    startIcon={<SendOutlinedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => {
                      if (tierBudget)
                        sendCollection(Number(tierBudget), searchParams.get("country") ?? "US");
                    }}
                  >
                    {isMobile ? "Send Gifts" : "Send This Collection"}
                  </GfgButton>
                ) : (
                  <Button
                    id={TOUR_TARGET_IDS.sendGifts}
                    variant="contained"
                    className="bg-background-dark ml-auto"
                    onClick={handleCreateCampaign}
                  >
                    <FeaturedSeasonalGiftsIcon className="mr-3 p-0.5" />
                    Send Gifts
                  </Button>
                )}
              </PermissionGate>
            </>
          )}
        </Toolbar>
      </div>

      {/* Mobile Drawer (temporary) */}
      {isMobile && (
        <MuiDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleMobileDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH_FULL,
              boxSizing: "border-box",
              // The nav list owns the scroll; the paper must not scroll as a whole
              // or the footer (profile + Support) would scroll away with it.
              overflowY: "hidden",
            },
          }}
        >
          {drawerContent}
        </MuiDrawer>
      )}

      {/* Desktop Drawer (permanent) */}
      {!isMobile && (
        <MuiDrawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              transition: "width 0.2s, top 0.2s",
              overflowX: "hidden",
              // The nav list owns the scroll; see the mobile paper above.
              overflowY: "hidden",
              // Push sidebar down when the impersonation banner is visible
              top: isImpersonating ? IMP_BAR_HEIGHT : 0,
              height: isImpersonating ? `calc(100% - ${IMP_BAR_HEIGHT}px)` : "100%",
            },
          }}
        >
          {drawerContent}
        </MuiDrawer>
      )}

      {/* Main Content */}
      {/* Padding comes from .gfg-app-main (--app-main-padding in index.css), not a p-* utility:
          full-bleed surfaces such as the impact report's .irv-doc cancel that exact token, so the
          value has to live in one place both sides can read. */}
      <main
        className="gfg-app-main flex-1 transition-all duration-200 overflow-x-hidden"
        style={{
          marginLeft: 0,
          marginTop: appBarHeight + (isImpersonating ? IMP_BAR_HEIGHT : 0),
        }}
      >
        {/* Above the routed content rather than on one page: a client whose campaign has stopped
            funding itself needs to be told wherever they happen to be, and the email that would
            otherwise tell them is blocked on a template. It renders nothing when nothing is wrong. */}
        <FundingBanner />
        <Outlet />
      </main>
    </div>
  );
}

// TourProvider wraps DrawerLayout (rather than living inside it) because the guided tour has
// to survive route changes the layout triggers — steps 4 and 5 navigate to /account and back,
// which would remount a provider nested inside the routed content. SpotlightTour renders here,
// inside the provider, because it calls useTour().
export default function Drawer() {
  return (
    <TourProvider>
      <HelpCenter>
        <DrawerLayout />
      </HelpCenter>
      <SpotlightTour />
    </TourProvider>
  );
}
